import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, MessageSquare, Mic, Phone, Database, ShoppingBag, CalendarCheck, FileText, Globe } from "lucide-react";
import MarketingLayout from "./MarketingLayout";
import Seo, { ORG_JSON_LD } from "./Seo";

/**
 * Shared template for all /use-cases/<industry> pages.
 * Each industry passes in its own data and we render a complete, SEO-friendly
 * use-case landing page with hero, capabilities, workflow, FAQ, CTA.
 */
export default function IndustryPage(props) {
  const {
    slug,
    industry,
    seoTitle,
    seoDescription,
    seoKeywords,
    accent = "#0044FF",
    Icon,
    hero,                 // { headline, sub }
    painPoints = [],      // [{ Icon, title, desc }]
    capabilities = [],    // [{ Icon, title, desc }]
    sampleChats = [],     // [{ q, a }]
    sources = [],         // strings - things the agent learns from
    integrations = [],    // strings
    metrics = [],         // [{ value, label }]
    faqs = [],            // [{ q, a }]
  } = props;

  const PAGE_LD = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: `https://hellosir.ai/use-cases/${slug}`,
    name: seoTitle,
    description: seoDescription,
    isPartOf: ORG_JSON_LD,
    about: industry,
  };

  const FAQ_LD = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <MarketingLayout>
      <Seo
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        path={`/use-cases/${slug}`}
        jsonLd={FAQ_LD ? [PAGE_LD, FAQ_LD] : PAGE_LD}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#E2E8F0] bg-gradient-to-br from-[#F0F4FF] via-white to-[#FFF1F8]">
        <div className="absolute -top-32 left-1/4 w-[480px] h-[480px] rounded-full blur-3xl opacity-40 animate-hs-blob"
             style={{ background: `radial-gradient(circle, ${accent} 0%, transparent 70%)` }} aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-[6px] flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>
              <Icon className="w-6 h-6" />
            </div>
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: accent }}>
              {industry} · use case
            </span>
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl mt-6 leading-[0.95] tracking-tight">
            {hero.headline}
          </h1>
          <p className="text-lg text-[#4B5563] mt-6 max-w-3xl">{hero.sub}</p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to="/signup" className="btn-primary inline-flex items-center gap-2" data-testid={`${slug}-cta-primary`}>
              Try free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-outline">Book a demo</Link>
          </div>

          {/* Channel chips */}
          <div className="flex flex-wrap gap-4 mt-10 text-sm font-medium">
            {[
              { label: "Text Chatbot", Icon: MessageSquare, c: "#0044FF" },
              { label: "Voice Chatbot", Icon: Mic, c: "#10B981" },
              { label: "AI Calling Agent", Icon: Phone, c: "#7C3AED" },
            ].map((c) => (
              <span key={c.label} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0]">
                <c.Icon className="w-4 h-4" style={{ color: c.c }} /> {c.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      {painPoints.length > 0 && (
        <section className="py-16 lg:py-20 border-b border-[#E2E8F0]">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">// the problem</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-3 max-w-2xl">
              Common headaches in {industry.toLowerCase()}.
            </h2>
            <div className="grid md:grid-cols-3 gap-5 mt-10">
              {painPoints.map((p) => (
                <div key={p.title} className="rounded-[6px] bg-white border border-[#E2E8F0] p-6">
                  <div className="w-11 h-11 rounded-[6px] flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}>
                    <p.Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display font-bold text-lg mt-5">{p.title}</h3>
                  <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CAPABILITIES */}
      <section className="py-16 lg:py-20 border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">// what your agent will do</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-3 max-w-3xl">
            A purpose-built {industry.toLowerCase()} agent, ready in minutes.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {capabilities.map((c) => (
              <div key={c.title} className="rounded-[6px] bg-white border border-[#E2E8F0] p-6 hover:border-[#030712] transition-colors">
                <div className="w-11 h-11 rounded-[6px] flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}>
                  <c.Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display font-bold text-lg mt-5">{c.title}</h3>
                <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / SAMPLE CHAT */}
      {sampleChats.length > 0 && (
        <section className="py-16 lg:py-20 border-b border-[#E2E8F0] bg-[#F9FAFB]">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">// real-world example</span>
              <h2 className="font-display font-bold text-3xl md:text-4xl mt-3">See it in action.</h2>
              <p className="text-[#4B5563] mt-4 leading-relaxed">
                Trained on your knowledge base, the agent answers in your customer's language, follows your policies, and hands off to a human when it
                should. This is what an actual {industry.toLowerCase()} chat looks like.
              </p>
              <ul className="mt-6 space-y-2 text-sm">
                {(sources.length ? sources : ["Your website", "PDFs & documents", "Live CRM / database"]).map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5" style={{ color: accent }} />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7 rounded-[6px] bg-white border border-[#E2E8F0] overflow-hidden shadow-sm">
              <div className="px-5 py-3 border-b border-[#E2E8F0] bg-[#0F1115] text-white flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
                  <span className="text-xs font-mono uppercase tracking-widest">live agent · {industry}</span>
                </div>
                <span className="text-[11px] font-mono text-white/60">trained on your KB</span>
              </div>
              <div className="p-5 space-y-4">
                {sampleChats.map((m, i) => (
                  <React.Fragment key={i}>
                    <div className="flex gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#F3F4F6] flex items-center justify-center text-[10px] font-bold text-[#0F1115] shrink-0">C</div>
                      <div className="flex-1 px-4 py-2.5 rounded-2xl rounded-tl-sm bg-[#F3F4F6] text-sm">{m.q}</div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: accent }}>
                        <MessageSquare className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="flex-1 px-4 py-2.5 rounded-2xl rounded-tl-sm text-sm" style={{ background: `${accent}10`, border: `1px solid ${accent}26` }}>
                        {m.a}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* METRICS */}
      {metrics.length > 0 && (
        <section className="py-12 lg:py-16 border-b border-[#E2E8F0]">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {metrics.map((m) => (
              <div key={m.label}>
                <div className="font-display font-extrabold text-4xl md:text-5xl" style={{ color: accent }}>{m.value}</div>
                <div className="text-sm text-[#4B5563] mt-2">{m.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* INTEGRATIONS / DATA SOURCES */}
      {integrations.length > 0 && (
        <section className="py-16 lg:py-20 border-b border-[#E2E8F0]">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">// data & integrations</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-3 max-w-3xl">
              Hooks into the tools you already run.
            </h2>
            <div className="flex flex-wrap gap-2 mt-8">
              {integrations.map((i) => (
                <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-sm">
                  <Database className="w-3.5 h-3.5" style={{ color: accent }} /> {i}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-16 lg:py-20 border-b border-[#E2E8F0] bg-[#F9FAFB]">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">// frequently asked</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-3">{industry} FAQs.</h2>
            <div className="mt-10 space-y-3">
              {faqs.map((f) => (
                <details key={f.q} className="rounded-[6px] bg-white border border-[#E2E8F0] p-5 group">
                  <summary className="font-display font-bold text-base cursor-pointer flex items-center justify-between gap-4 list-none">
                    {f.q}
                    <span className="text-2xl font-light text-[#4B5563] group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-sm text-[#4B5563] mt-3 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl leading-[1.05]">Ready to launch your {industry.toLowerCase()} agent?</h2>
            <p className="text-[#4B5563] mt-3">Free trial · no card required · live in under 10 minutes.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/signup" className="btn-primary text-base">Start free</Link>
            <Link to="/contact" className="btn-outline">Talk to sales</Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

/** Generic icon helper exported for industry files */
export const SharedIcons = {
  MessageSquare, Mic, Phone, Database, ShoppingBag, CalendarCheck, FileText, Globe, Check, ArrowRight,
};
